// Phase 1 source for /preview/:cellPath. Uses Vite's import.meta.glob to
// bundle every content/**/*.md at build time. Phase 4 will introduce a
// projector-backed loader (GET /content/:path) for the Co-Editor surface.
//
// Under bun:test (no Vite bundler), the literal `import.meta.glob(...)`
// call is not rewritten and throws at runtime; the try/catch in
// loadCellMap returns an empty record so the helpers compile and unit
// tests still cover the encode/decode pair. Real glob coverage is the
// Playwright smoke test (Task 18) which runs against the Vite dev server.

type CellLoaderMap = Record<string, () => Promise<string>>;

// `import.meta.glob` is a Vite compile-time MACRO, not a runtime function.
// The static analyzer rewrites every literal `import.meta.glob(...)` call
// site into a frozen object of `{key: () => import(...)}` entries; there
// is no `glob` property on `import.meta` at runtime, so a `typeof
// import.meta.glob === 'function'` guard always reports `'undefined'` in
// the browser and short-circuits us into an empty map. The right shape is
// to call the macro directly and let the try/catch absorb bun:test (where
// no Vite transform runs and the literal `import.meta.glob(...)` syntax
// throws a `TypeError`).
//
// Aliasing `import.meta` through a typed const (`const meta = import.meta`)
// also hides the call from Vite's transformer — keep the call site in its
// canonical `import.meta.glob(...)` form.
//
// Path is relative to this file (apps/web/src/lib/cell-loader.ts).
// Repo root is ../../../../, so content/ is ../../../../content/.
// Leading-slash globs are rooted at the Vite project (apps/web/) and
// don't reach the repo-root content/ directory — the relative form
// is the documented escape hatch for files outside the project root.
function loadCellMap(): CellLoaderMap {
  try {
    return import.meta.glob('../../../../content/**/*.md', {
      query: '?raw',
      import: 'default',
      eager: false,
    }) as CellLoaderMap;
  } catch {
    return {};
  }
}

const cells = loadCellMap();

// Vite may report keys in either resolved-absolute or literal-relative
// form depending on options. Both contain `/content/<relPath>` somewhere
// in the string, so strip everything up to and including `content/`.
function relativizeKey(key: string): string {
  const match = key.match(/(?:^|\/)content\/(.+)$/);
  return match ? match[1] : key;
}

export function listCells(): string[] {
  return Object.keys(cells).map(relativizeKey).sort();
}

export async function loadCell(relPath: string): Promise<string> {
  const target = `content/${relPath}`;
  const entry = Object.entries(cells).find(([key]) => key.endsWith(target));
  if (!entry) throw new Error(`cell not found: ${relPath}`);
  return entry[1]();
}

// Per-segment URL encoding. React Router decodes percent-encoded :params
// automatically on the read side; we use these helpers when building
// links and listing cells.
export const encodeCellPath = (p: string) =>
  p.split('/').map(encodeURIComponent).join('/');

export const decodeCellPath = (p: string) =>
  p.split('/').map(decodeURIComponent).join('/');
